import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatCheckboxChange, MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, debounceTime, filter, takeUntil } from 'rxjs';
import { AxiosError } from 'axios';
import {
  ClientsService,
  ListClientItem,
} from 'src/app/services/clients/clients.service';
import { HelpersService } from 'src/app/services/helpers/helpers.service';
import {
  LawFirmItem,
  LawFirmService,
} from 'src/app/services/law-firm/law-firm.service';
import { RolesService } from 'src/app/services/roles/roles.service';
import {
  UserDataTableItem,
  UsersService,
} from 'src/app/services/users/users.service';
import { PayeePopupComponent } from '../../../../shared/payee-popup/payee-popup.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PayeeFormFieldComponent } from '../../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import {
  MatAutocompleteTrigger,
  MatAutocomplete,
  MatOption,
} from '@angular/material/autocomplete';
import { PayeeMiniIconButtonComponent } from '../../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { PayeeIconComponent } from '../../../../shared/payee-icon/payee-icon.component';
import { PayeeButtonComponent } from '../../../../shared/payee-buttons/payee-button/payee-button.component';

type Country = {
  iso: string;
  name: string;
};

@Component({
  selector: 'app-edit-user-dialog',
  templateUrl: './edit-user-dialog.component.html',
  styleUrls: ['./edit-user-dialog.component.scss'],
  imports: [
    PayeePopupComponent,
    MatProgressSpinner,
    FormsModule,
    ReactiveFormsModule,
    PayeeFormFieldComponent,
    MatCheckbox,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
    PayeeButtonComponent,
  ],
})
export class EditUserDialogComponent implements OnInit, OnDestroy {
  private user = inject<UserDataTableItem>(MAT_DIALOG_DATA);
  private fb = inject(FormBuilder);
  private snackbar = inject(MatSnackBar);
  private helpersService = inject(HelpersService);
  private rolesService = inject(RolesService);
  private clientsService = inject(ClientsService);
  private usersService = inject(UsersService);
  private lawFirmService = inject(LawFirmService);
  private readonly destroy = new Subject<void>();
  private readonly clientSearch$ = new Subject<string>();
  private readonly lawFirmSearch$ = new Subject<string>();

  dialogRef = inject<MatDialogRef<EditUserDialogComponent>>(MatDialogRef);

  readonly formGroup = this.fb.group({
    email: this.fb.control(this.user.email, {
      validators: [Validators.required, Validators.email],
      nonNullable: true,
    }),
    name: this.fb.control(this.user.user_name, {
      validators: Validators.required,
      nonNullable: true,
    }),
    phone: this.fb.control(this.user.phone_number ?? '', {
      validators: this.helpersService.phoneNumberValidator,
      nonNullable: true,
    }),
    roles: this.fb.control(
      this.user.roles.map((r) => r.name),
      { validators: Validators.minLength(1), nonNullable: true },
    ),
    attorneyCountries: this.fb.control<Country[]>(
      this.user.attorney_countries,
      { nonNullable: true },
    ),
    clients: this.fb.control<ListClientItem[]>(
      this.user.clients.map((c) => ({ uuid: c.uuid, name: c.name })),
      { nonNullable: true },
    ),
    law_firms: this.fb.control<LawFirmItem[]>(this.user.law_firms.concat(), {
      nonNullable: true,
    }),
  });

  readonly clientFilter = this.fb.control('', { nonNullable: true });
  clientsLoading = false;
  filteredClients: ListClientItem[] = [];

  readonly lawFirmFilter = this.fb.control('', { nonNullable: true });
  lawFirmsLoading = false;
  lawFirmOptions: LawFirmItem[] = [];

  roleOptions: { name: string }[] = [];
  readonly attorneyCountryOptions: Country[] = [
    { iso: 'HU', name: 'Magyarország' },
    { iso: 'SK', name: 'Szlovákia' },
  ];

  submitLoading = false;
  readonly initLoading = signal(false);

  get email() {
    return this.formGroup.controls.email;
  }
  get name() {
    return this.formGroup.controls.name;
  }
  get phone() {
    return this.formGroup.controls.phone;
  }
  get roles() {
    return this.formGroup.controls.roles;
  }
  get clients() {
    return this.formGroup.controls.clients;
  }
  get law_firms() {
    return this.formGroup.controls.law_firms;
  }
  get attorneyCountries() {
    return this.formGroup.controls.attorneyCountries;
  }

  get userCanViewAttorney(): boolean {
    return ['attorney', 'Super-Admin'].some((role) =>
      this.roles.value.includes(role),
    );
  }

  async ngOnInit(): Promise<void> {
    this.clientSearch$
      .pipe(
        takeUntil(this.destroy),
        filter((v): v is string => !!v && v.length > 2),
        debounceTime(1000),
      )
      .subscribe({
        next: async (filter) => await this.setClientOptions(filter),
      });

    this.lawFirmSearch$
      .pipe(
        takeUntil(this.destroy),
        filter((v): v is string => !!v && v.length > 2),
        debounceTime(1000),
      )
      .subscribe({
        next: async (filter) => await this.setLawFirmOptions(filter),
      });

    await this.setRoleOptions();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
    this.clientSearch$.complete();
    this.lawFirmSearch$.complete();
  }

  onClientSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.clientSearch$.next(value);
  }

  onLawFirmSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.lawFirmSearch$.next(value);
  }

  isRoleSelected(role: string): boolean {
    return this.roles.value.includes(role);
  }

  toggleCountry = (event: MatCheckboxChange, country: Country): void => {
    const checkboxState: boolean = event.checked;
    const countries: Country[] = this.attorneyCountries.value;

    if (checkboxState && !this.isCountrySelected(country)) {
      countries.push(country);
    } else if (!checkboxState && this.isCountrySelected(country)) {
      countries.splice(
        countries.findIndex((c) => c.iso === country.iso),
        1,
      );
    }
    this.attorneyCountries.patchValue(countries);
  };

  isCountrySelected = (country: Country): boolean => {
    return !!this.attorneyCountries.value.find(
      (c: Country) => c.iso === country.iso,
    );
  };

  toggleRole(event: MatCheckboxChange, role: string): void {
    const checkboxState: boolean = event.checked;
    const roles: string[] = this.roles.value;

    if (checkboxState && !roles.includes(role)) {
      roles.push(role);
    }
    if (!checkboxState && roles.includes(role)) {
      roles.splice(roles.indexOf(role), 1);
    }
    this.roles.patchValue(roles);
  }

  selectClient(client: ListClientItem): void {
    const clients: ListClientItem[] = this.clients.value;
    if (clients.find((c) => c.uuid === client.uuid)) {
      return;
    }
    clients.push(client);
    this.clients.patchValue(clients);
  }

  unselectClient(client: ListClientItem): void {
    const clients: ListClientItem[] = this.clients.value;
    const index = clients.findIndex((c) => c.uuid === client.uuid);
    if (index === -1) {
      return;
    }
    clients.splice(index, 1);
    this.clients.patchValue(clients);
  }

  clearClientFilter(): void {
    this.filteredClients = [];
    this.clientFilter.patchValue('');
  }

  selectLawFirm(lawFirm: LawFirmItem): void {
    const law_firms: LawFirmItem[] = this.law_firms.value;
    if (law_firms.find((lf) => lf.id === lawFirm.id)) {
      return;
    }
    law_firms.push(lawFirm);
    this.law_firms.patchValue(law_firms);
  }

  unselectLawFirm(lawFirm: LawFirmItem): void {
    const law_firms: LawFirmItem[] = this.law_firms.value;
    const index = law_firms.findIndex((lf) => lf.id === lawFirm.id);
    if (index === -1) {
      return;
    }
    law_firms.splice(index, 1);
    this.law_firms.patchValue(law_firms);
  }

  clearLawFirmFilter(): void {
    this.lawFirmOptions = [];
    this.lawFirmFilter.patchValue('');
  }

  async submit() {
    if (this.formGroup.invalid || this.submitLoading) {
      return;
    }

    const { email, name, phone, roles, attorneyCountries, clients, law_firms } =
      this.formGroup.getRawValue();

    try {
      this.submitLoading = true;

      await this.usersService.updateUser(this.user.user_uuid, {
        client_ids: clients.map((c) => c.uuid),
        email,
        name,
        roles: roles.filter((r) =>
          this.roleOptions.find((rop) => rop.name === r),
        ),
        phone: phone || undefined,
        attorney_countries: attorneyCountries.map((c) => c.iso),
        law_firms: law_firms.map((lf) => lf.id),
      });

      this.dialogRef.close(email);
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 422) {
          const errors: Record<string, string[]> = error.response.data.errors;
          Object.entries(errors).forEach(([key, values]) => {
            const formControl = this.formGroup.get(key);
            if (!formControl) {
              this.formGroup.setErrors({
                backend: values[0],
              });
            } else {
              formControl.setErrors({
                backend: values[0],
              });
            }
          });

          return;
        }
      }
      console.error('Error while updating user', error);
      this.snackbar.open('Valami hiba történt mentéskor!', 'OK', {
        duration: 5000,
      });
    } finally {
      this.submitLoading = false;
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private async setClientOptions(name: string): Promise<void> {
    try {
      this.clientsLoading = true;
      const result = await this.clientsService.listClients({
        name,
      });
      this.filteredClients = result.clients;
    } catch (error) {
      console.error('Error while loading clients', error);
    } finally {
      this.clientsLoading = false;
    }
  }

  private async setLawFirmOptions(name: string): Promise<void> {
    try {
      this.lawFirmsLoading = true;
      this.lawFirmOptions = await this.lawFirmService.listLawFirms(name);
    } catch (error) {
      console.error('Error while loading law firms', error);
    } finally {
      this.lawFirmsLoading = false;
    }
  }

  private async setRoleOptions(): Promise<void> {
    try {
      this.initLoading.set(true);

      const roleResult = await this.rolesService.getRoles();
      this.roleOptions = roleResult.roles;
    } catch (error) {
      console.error('Error while initializing', error);
    } finally {
      this.initLoading.set(false);
    }
  }
}
